"use client";

import Image from "next/image";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import LeftArrowWhiteCircleIcon from "~/icons/left-arrow-white-circle.svg";
import styles from "./SpecializationCard.module.scss";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import "swiper/css/grid";
import "swiper/css";
import Markdown from "@/components/atoms/Markdown";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";

type Specialization = {
  Image?: {
    enabled: boolean;
    source?: {
      data?: Array<{ attributes: { url: string } }>;
    };
    alt?: string;
    url?: string;
    title?: string;
  };
  Title?: {
    enabled: boolean;
    text: string;
  };
  SponsoredImage?: {
    data?: {
      attributes: {
        url: string;
      };
    };
  };
};


export function SpecializationCard({
  SlideContent,
}: {
  SlideContent: Specialization;
}) {

  const title = SlideContent?.Title?.text.replace(/\n/gi, "  \n");

  const GAEventsHandler = () => {
    pushToDataLayer({
      'event':'card_interaction',
      'cta_text': title,
      'section_name': 'Key Specializations',
      'page_type': getGlobalStateValue('pageType') 
      });
  }

  return (
    <div
      className={`${styles.SpecializationCard__background} flex flex-col justify-between`}
    >
      {SlideContent?.Image?.enabled && (
        <div className={ `${styles.SpecializationCard__InnerImage}, flex justify-between`}>
          <Image
            src={SlideContent?.Image?.source?.data?.[0]?.attributes?.url}
            alt={SlideContent?.Image?.alt ||'Specialization Card'}
            width={60}
            height={60}
            title="Specialization Card"
          />
          {SlideContent?.SponsoredImage?.data?.attributes?.url && (
            <div className={styles['sponsored-image-wrapper']}>
            <Image
              src={SlideContent?.SponsoredImage?.data?.attributes.url}
              alt= "logo"
              width={80}
              height={60}
              title="logo"
              className={styles['sponsored-image']}
            />
            </div>
          )
          }
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        {SlideContent?.Title?.enabled && (
          <h3 className={`${styles.SpecializationCard__InnerTitle} font-semibold`}
          >
            <Markdown>
              {title}
            </Markdown>
          </h3>
        )}
        {!!SlideContent?.Image?.url && (
          <a
            onClick={GAEventsHandler}
            href={SlideContent?.Image?.url || ""}
            title={
              SlideContent?.Image?.title ||
              SlideContent?.Title?.text?.replace(/\n/gi, "")
            }
            
          >
            <div
              className={`shrink-0 ${styles.SpecializationCard__InnerShowMoreIcon}
        font-semibold`}
            >
              <Image src={LeftArrowWhiteCircleIcon} alt="show more" title="show more"/>
            </div>
          </a>
        )}
      </div>
    </div>
  );
}

export default function SpecializationCarousel({
  data,
}: {
  data: SpecializationAttributes[];
}) {
  
  return (
    <div id="Carousel">
      <Swiper
        modules={[Pagination, Autoplay]}
        spaceBetween={20}
        autoplay={{
          delay: 6000,
        }}
        slidesPerView={"auto"}
        pagination={{ clickable: true, bulletElement: "div" }}
        className="Carousel__blueBullet Carousel__specialization"
        breakpoints={{
          320: {
            slidesPerView: 1.2,
            spaceBetween: 10,
          },
          768: {
            slidesPerView: 2.3,
            spaceBetween: 15,
          },
          1024: {
            slidesPerView: 3.3,
            spaceBetween: 20,
          },
          1280: {
            slidesPerView: 4.3,
            spaceBetween: 10,
          },
          2560: {
            slidesPerView: 5.3,
            spaceBetween: 10,
          }
        }}
      >
        {data?.map((slideContent: SpecializationAttributes, index: number) => (
          <SwiperSlide className="mr-[20px]" key={index}>
            <SpecializationCard SlideContent={slideContent.attributes} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
